import type { ReactNode } from "react";
import { Button } from "./Button";
import { surface } from "./classes";

/*
 * The states every page has. Loading is a status the live region will
 * read; an error is an alert that says what failed and what to do; empty
 * says what would fill it.
 */

export function LoadingState({ label = "Loading…" }: { label?: ReactNode }) {
  return (
    <div role="status" aria-live="polite" className="flex items-center gap-3 py-8 text-muted">
      <span className="h-4 w-4 animate-spin rounded-full border-2 border-line border-t-accent" aria-hidden="true" />
      {label}
    </div>
  );
}

/** What failed, what to do, and the request id to quote to support (OPS-09). */
export function ErrorState({ title, error, onRetry, retryLabel = "Try again" }: { title: ReactNode; error?: unknown; onRetry?: () => void; retryLabel?: ReactNode }) {
  const e = error as { message?: string; requestId?: string } | null | undefined;
  return (
    <div role="alert" className="rounded-xl border border-danger bg-danger-surface p-5 text-ink">
      <p className="font-medium">{title}</p>
      {e?.message && <p className="mt-1 text-sm">{e.message}</p>}
      {e?.requestId && (
        <p className="mt-1 text-sm">
          Request id: <code className="break-all font-mono">{e.requestId}</code>
        </p>
      )}
      {onRetry && (
        <Button className="mt-3" onClick={onRetry}>
          {retryLabel}
        </Button>
      )}
    </div>
  );
}

export function EmptyState({ title, hint, action }: { title: ReactNode; hint?: ReactNode; action?: ReactNode }) {
  return (
    <div className={`${surface} border-dashed px-6 py-12 text-center`}>
      <p className="text-base font-medium text-ink">{title}</p>
      {hint && <p className="mt-1 text-sm text-muted">{hint}</p>}
      {action && <div className="mt-4 flex justify-center">{action}</div>}
    </div>
  );
}
